import { elbowAngle } from "./math";
import {
  LandmarkId,
  type EngineEvent,
  type Point,
  type PoseFrame,
  type PushupEngineConfig,
  type RepQuality,
  type RepState,
} from "./types";

const DEFAULT_THRESHOLDS: Record<PushupEngineConfig["mode"], number> = {
  full: 92,
  knee: 108,
};

/**
 * Client-side pushup rep engine.
 *
 * Ported from the decompiled reference app and re-expressed cleanly:
 * - tracks a rep lifecycle state machine,
 * - uses the minimum elbow angle reached at the bottom of a rep as the depth
 *   signal (full ≤ 92°, knee ≤ 108°),
 * - emits structured events (`rep`, `form-error`, `state`) that the UI layer
 *   renders as real-time feedback.
 *
 * NOT YET IMPLEMENTED (documented next steps, see docs/reverse-engineering.md):
 * - body straightness / hip-sag detection,
 * - "no pause at the bottom" tempo check,
 * - phone-motion guard (accelerometer),
 * - framing check (hips/feet in frame).
 *
 * The engine is pure and framework-agnostic: it runs on any client and is
 * trivially unit-testable. It never touches the network.
 */
export class PushupEngine {
  private state: RepState = "waitingForUp";
  private reps = 0;
  private lastPoseAt = -1;
  private bestDepth = 180;
  private prevDepth = 180;
  private latestDepth = 180;

  readonly config: Required<Omit<PushupEngineConfig, "mode">> & Pick<PushupEngineConfig, "mode">;

  constructor(config: PushupEngineConfig) {
    this.config = {
      elbowDepthThresholdDeg: config.elbowDepthThresholdDeg ?? DEFAULT_THRESHOLDS[config.mode],
      depthHysteresisDeg: config.depthHysteresisDeg ?? 8,
      noPoseTimeoutMs: config.noPoseTimeoutMs ?? 1500,
      mode: config.mode,
    };
  }

  get repCount(): number {
    return this.reps;
  }

  get currentState(): RepState {
    return this.state;
  }

  /** Latest elbow angle in degrees (180 = straight arm, lower = deeper). */
  get currentDepthDeg(): number {
    return this.latestDepth;
  }

  reset(): void {
    this.state = "waitingForUp";
    this.reps = 0;
    this.lastPoseAt = -1;
    this.bestDepth = 180;
    this.prevDepth = 180;
    this.latestDepth = 180;
  }

  /** Full teardown — resets all state. Call when the session is finished. */
  destroy(): void {
    this.reset();
  }

  /**
   * Feed one frame of pose landmarks into the engine. Returns any events that
   * should be surfaced to the user (a completed rep, a form error, a state
   * change).
   *
   * Transitions are hysteresis-gated so that top-of-rep noise (a resting user
   * with slightly wobbly, locked arms) can never start a phantom rep:
   *
   *   - a rep attempt only begins when the elbow bends below
   *     `threshold + hysteresis` (i.e. the user actually lowered themselves),
   *   - the bottom is only entered below the depth threshold,
   *   - a rep only completes once the elbow returns above
   *     `threshold + hysteresis`.
   */
  feedFrame(frame: PoseFrame): EngineEvent[] {
    const events: EngineEvent[] = [];
    const depth = this.currentDepth(frame);
    const hasPose = depth !== null;

    if (!hasPose) {
      return this.handleNoPose(frame, events);
    }

    this.lastPoseAt = frame.timestamp;
    this.latestDepth = depth;

    const downThreshold = this.config.elbowDepthThresholdDeg;
    const upThreshold = downThreshold + this.config.depthHysteresisDeg;

    switch (this.state) {
      case "waitingForUp": {
        // Wait until the user is in the top (arms straight) position.
        if (depth > upThreshold) {
          this.transitionTo("up", events);
        }
        break;
      }

      case "up": {
        // A rep attempt starts only when the arms bend meaningfully below the
        // ready position — resting jitter at the top is ignored.
        if (depth <= upThreshold) {
          this.bestDepth = depth;
          this.transitionTo("descent", events);
        }
        break;
      }

      case "descent": {
        this.bestDepth = Math.min(this.bestDepth, depth);
        if (depth <= downThreshold) {
          this.transitionTo("down", events);
        } else if (depth > upThreshold) {
          // Reversed before reaching the target depth → shallow attempt.
          this.failTooShallow(events);
        }
        break;
      }

      case "down": {
        this.bestDepth = Math.min(this.bestDepth, depth);
        if (depth > upThreshold) {
          this.completeRep(events);
        } else if (depth > downThreshold && depth > this.prevDepth) {
          this.transitionTo("ascent", events);
        }
        break;
      }

      case "ascent": {
        if (depth <= downThreshold) {
          // Re-entered the bottom band mid-rep (the rep is not complete yet).
          this.transitionTo("down", events);
        } else if (depth > upThreshold) {
          this.completeRep(events);
        }
        break;
      }
    }

    this.prevDepth = depth;
    return events;
  }

  private completeRep(events: EngineEvent[]): void {
    const quality = this.quality();
    this.reps += 1;
    events.push({
      type: "rep",
      rep: this.reps,
      quality,
      message:
        quality === "deep" ? "Great depth — nice rep!" : "Rep completed, but go deeper next time.",
    });
    this.transitionTo("up", events);
    this.bestDepth = 180;
  }

  private failTooShallow(events: EngineEvent[]): void {
    if (this.bestDepth > this.config.elbowDepthThresholdDeg) {
      events.push({
        type: "form-error",
        code: "TOO_SHALLOW",
        message: `Too shallow — elbow never reached ${this.config.elbowDepthThresholdDeg}° (best ${Math.round(this.bestDepth)}°)`,
      });
    }
    this.transitionTo("ascent", events);
  }

  private transitionTo(next: RepState, events: EngineEvent[]): void {
    if (this.state === next) {
      return;
    }
    this.state = next;
    events.push({ type: "state", state: next });
  }

  private currentDepth(frame: PoseFrame): number | null {
    const left = this.armDepth(
      frame,
      LandmarkId.LeftShoulder,
      LandmarkId.LeftElbow,
      LandmarkId.LeftWrist,
    );
    const right = this.armDepth(
      frame,
      LandmarkId.RightShoulder,
      LandmarkId.RightElbow,
      LandmarkId.RightWrist,
    );
    if (left === null && right === null) {
      return null;
    }
    const candidates = [left, right].filter((d): d is number => d !== null);
    return Math.min(...candidates);
  }

  private armDepth(
    frame: PoseFrame,
    shoulderId: LandmarkId,
    elbowId: LandmarkId,
    wristId: LandmarkId,
  ): number | null {
    const shoulder = frame.landmarks[shoulderId];
    const elbow = frame.landmarks[elbowId];
    const wrist = frame.landmarks[wristId];
    if (!shoulder || !elbow || !wrist) {
      return null;
    }
    if (this.visible(shoulder) && this.visible(elbow) && this.visible(wrist)) {
      return elbowAngle(shoulder, elbow, wrist);
    }
    return null;
  }

  private visible(point: Point): boolean {
    return point.visibility >= 0.5;
  }

  private quality(): RepQuality {
    return this.bestDepth <= this.config.elbowDepthThresholdDeg ? "deep" : "shallow";
  }

  private handleNoPose(frame: PoseFrame, events: EngineEvent[]): EngineEvent[] {
    if (
      this.state !== "waitingForUp" &&
      this.lastPoseAt >= 0 &&
      frame.timestamp - this.lastPoseAt > this.config.noPoseTimeoutMs
    ) {
      this.state = "waitingForUp";
      this.bestDepth = 180;
      events.push({ type: "state", state: "waitingForUp" });
    }
    return events;
  }
}

export function createPushupEngine(config: PushupEngineConfig): PushupEngine {
  return new PushupEngine(config);
}
